import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { RENEWAL_PERIODS, SUBSCRIPTION_STATUSES } from '@subs/shared';

import {
  subscriptions,
  vendorGroups,
  type Subscription,
  type SubscriptionInput,
  type VendorGroup,
} from '../api/client';
import { useToast } from './Toast';
import { TagInput } from './TagInput';
import { Button, Field, Input, Modal, Provenance, Select, Textarea } from './ui';

const EMPTY: SubscriptionInput = {
  name: '',
  groupId: null,
  url: null,
  purpose: null,
  initialCost: 0,
  renewalCost: 0,
  renewalPeriod: 'MONTH',
  currency: 'USD',
  expiresAt: '',
  expiresHighlightAfter: null,
  status: 'ACTIVE',
  blockedReason: null,
  note: null,
  assignedUserEmails: [],
};

/** `<input type="date">` value for an ISO timestamp (UTC). */
function toDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

function fromSubscription(subscription: Subscription): SubscriptionInput {
  return {
    name: subscription.name,
    groupId: subscription.groupId,
    url: subscription.url,
    purpose: subscription.purpose,
    initialCost: subscription.initialCost,
    renewalCost: subscription.renewalCost,
    renewalPeriod: subscription.renewalPeriod,
    currency: subscription.currency,
    expiresAt: toDateInput(subscription.expiresAt),
    expiresHighlightAfter: toDateInput(subscription.expiresHighlightAfter) || null,
    status: subscription.status,
    blockedReason: subscription.blockedReason,
    note: subscription.note,
    assignedUserEmails: subscription.assignedUsers,
  };
}

interface FormState extends SubscriptionInput {
  id?: string;
}

function validate(state: FormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!state.name.trim()) errors.name = 'Name is required';
  if (!state.currency.trim() || state.currency.trim().length !== 3) errors.currency = '3-letter code, e.g. USD';
  if (!state.expiresAt) errors.expiresAt = 'Expiry date is required';
  if (state.renewalCost < 0 || state.initialCost < 0) errors.renewalCost = 'Cost cannot be negative';
  if (state.status === 'BLOCKED' && !state.blockedReason?.trim()) {
    errors.blockedReason = 'A reason is required when blocking';
  }
  if (state.url && !/^https?:\/\/\S+$/i.test(state.url)) errors.url = 'Must be an http(s) URL';
  return errors;
}

/**
 * Create/edit form. Credentials are deliberately NOT part of this form: they
 * are write-only and live behind the separate "Show credentials" action.
 */
export function SubscriptionFormModal({
  open,
  subscription,
  onClose,
}: {
  open: boolean;
  subscription: Subscription | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [state, setState] = useState<FormState>(() => (subscription ? fromSubscription(subscription) : EMPTY));
  const [touched, setTouched] = useState(false);

  // Re-seed the form whenever the modal is (re)opened for another subscription.
  const seedKey = `${subscription?.id ?? 'new'}-${open}`;
  useEffect(() => {
    if (!open) return;
    setState(subscription ? fromSubscription(subscription) : EMPTY);
    setTouched(false);
     
  }, [seedKey]);

  const { data: groups } = useQuery({
    queryKey: ['vendor-groups', 200],
    queryFn: ({ signal }) => vendorGroups.list({ limit: 200 }, signal),
    enabled: open,
    staleTime: 60_000,
  });

  const mutation = useMutation({
    mutationFn: (input: SubscriptionInput) => {
      const payload: SubscriptionInput = {
        ...input,
        name: input.name.trim(),
        currency: input.currency.trim().toUpperCase(),
        url: input.url?.trim() || null,
        purpose: input.purpose?.trim() || null,
        note: input.note?.trim() || null,
        blockedReason: input.status === 'BLOCKED' ? input.blockedReason?.trim() || null : null,
        expiresAt: new Date(`${input.expiresAt}T00:00:00.000Z`).toISOString(),
        expiresHighlightAfter: input.expiresHighlightAfter
          ? new Date(`${input.expiresHighlightAfter}T00:00:00.000Z`).toISOString()
          : null,
      };
      return subscription
        ? subscriptions.update(subscription.id, payload)
        : subscriptions.create(payload);
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      toast(subscription ? `Updated ${saved.name}` : `Created ${saved.name}`, 'success');
      onClose();
    },
    onError: (error: Error) => toast(error.message, 'error'),
  });

  const errors = validate(state);
  const set = <K extends keyof FormState>(field: K, value: FormState[K]) =>
    setState((current) => ({ ...current, [field]: value }));

  const submit = () => {
    setTouched(true);
    if (Object.keys(errors).length > 0) return;
    mutation.mutate(state);
  };

  const show = (field: string): string | undefined => (touched ? errors[field] : undefined);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={subscription ? 'Edit subscription' : 'New subscription'}
      description={subscription ? subscription.name : 'Everything except credentials lives here.'}
      footer={
        <>
          <Provenance createdBy={subscription?.createdBy ?? null} createdAt={subscription?.createdAt ?? null} />
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={mutation.isPending} onClick={submit}>
            {subscription ? 'Save changes' : 'Create subscription'}
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <div className="form-grid__full">
          <Field label="Name" htmlFor="sub-name" required error={show('name')}>
            <Input
              id="sub-name"
              value={state.name}
              aria-invalid={Boolean(show('name'))}
              onChange={(event) => set('name', event.target.value)}
              placeholder="GLM Coding Plan — acct5"
            />
          </Field>
        </div>

        <Field label="Vendor group" htmlFor="sub-group" hint="Bought-out instances of one vendor share it">
          <Select
            id="sub-group"
            value={state.groupId ?? ''}
            onChange={(event) => set('groupId', event.target.value || null)}
          >
            <option value="">— no group —</option>
            {(groups?.items ?? []).map((group: VendorGroup) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="URL" htmlFor="sub-url" error={show('url')}>
          <Input
            id="sub-url"
            type="url"
            value={state.url ?? ''}
            onChange={(event) => set('url', event.target.value || null)}
            placeholder="https://z.ai/subscription"
          />
        </Field>

        <Field label="Initial cost" htmlFor="sub-initial">
          <Input
            id="sub-initial"
            type="number"
            min="0"
            step="0.01"
            value={state.initialCost}
            onChange={(event) => set('initialCost', Number(event.target.value))}
          />
        </Field>

        <Field label="Renewal cost" htmlFor="sub-renewal" error={show('renewalCost')}>
          <Input
            id="sub-renewal"
            type="number"
            min="0"
            step="0.01"
            value={state.renewalCost}
            onChange={(event) => set('renewalCost', Number(event.target.value))}
          />
        </Field>

        <Field label="Renewal period" htmlFor="sub-period">
          <Select
            id="sub-period"
            value={state.renewalPeriod}
            onChange={(event) => set('renewalPeriod', event.target.value as (typeof RENEWAL_PERIODS)[number])}
          >
            {RENEWAL_PERIODS.map((period) => (
              <option key={period} value={period}>
                {period === 'MONTH' ? 'Monthly' : period === 'YEAR' ? 'Yearly' : 'One-off'}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Currency" htmlFor="sub-currency" required error={show('currency')}>
          <Input
            id="sub-currency"
            maxLength={3}
            value={state.currency}
            aria-invalid={Boolean(show('currency'))}
            onChange={(event) => set('currency', event.target.value.toUpperCase())}
          />
        </Field>

        <Field label="Expires at" htmlFor="sub-expires" required error={show('expiresAt')}>
          <Input
            id="sub-expires"
            type="date"
            value={state.expiresAt}
            aria-invalid={Boolean(show('expiresAt'))}
            onChange={(event) => set('expiresAt', event.target.value)}
          />
        </Field>

        <Field label="Highlight expiry from" htmlFor="sub-highlight" hint="Defaults to 14 days before expiry">
          <Input
            id="sub-highlight"
            type="date"
            value={state.expiresHighlightAfter ?? ''}
            onChange={(event) => set('expiresHighlightAfter', event.target.value || null)}
          />
        </Field>

        <Field label="Status" htmlFor="sub-status">
          <Select
            id="sub-status"
            value={state.status}
            onChange={(event) => set('status', event.target.value as (typeof SUBSCRIPTION_STATUSES)[number])}
          >
            {SUBSCRIPTION_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </Select>
        </Field>

        {state.status === 'BLOCKED' ? (
          <div className="form-grid__full">
            <Field
              label="Blocked reason"
              htmlFor="sub-reason"
              required
              error={show('blockedReason')}
              hint="Shown as the tooltip on the gray BLOCKED pill"
            >
              <Input
                id="sub-reason"
                value={state.blockedReason ?? ''}
                aria-invalid={Boolean(show('blockedReason'))}
                onChange={(event) => set('blockedReason', event.target.value)}
              />
            </Field>
          </div>
        ) : null}

        <div className="form-grid__full">
          <Field label="Assigned users" htmlFor="sub-users" hint="Enter to add an email, Backspace to remove the last">
            <TagInput
              label="Assigned user emails"
              values={state.assignedUserEmails ?? []}
              onChange={(next) => set('assignedUserEmails', next)}
              placeholder="dev@ismart.org"
            />
          </Field>
        </div>

        <div className="form-grid__full">
          <Field label="Note" htmlFor="sub-note">
            <Textarea
              id="sub-note"
              value={state.note ?? ''}
              onChange={(event) => set('note', event.target.value || null)}
            />
          </Field>
        </div>
      </div>
    </Modal>
  );
}
